<script setup lang="ts">
/**
 * InterfaceGuide Component
 *
 * Main orchestrator for the interactive tutorial overlay system.
 * Renders the spotlight and tooltip when a tutorial is active.
 * Handles keyboard navigation and manages tutorial flow.
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useTutorial } from '@/composables/useTutorial'
import TutorialSpotlight from './TutorialSpotlight.vue'
import TutorialTooltip from './TutorialTooltip.vue'
import type { SpotlightRect, TutorialId } from '@/types/tutorial'

const props = withDefaults(
  defineProps<{
    /** Tutorial context */
    context: TutorialId
  }>(),
  {}
)

const emit = defineEmits<{
  /** Emitted when tutorial is started */
  started: []
  /** Emitted when tutorial is completed */
  completed: []
  /** Emitted when tutorial is skipped */
  skipped: []
}>()

const tutorial = useTutorial()

// Reference to spotlight for cutout positioning
const spotlightRef = ref<InstanceType<typeof TutorialSpotlight> | null>(null)

// Cutout rect for tooltip positioning
const cutoutRect = ref<SpotlightRect | null>(null)

/**
 * Check if this tutorial context is currently active
 */
const isActiveForContext = computed(() => {
  return (
    tutorial.isActive.value &&
    tutorial.state.value.activeTutorial === props.context
  )
})

/**
 * Handle next step
 */
function handleNext(): void {
  if (tutorial.isLastStep.value) {
    tutorial.completeTutorial()
    emit('completed')
  } else {
    tutorial.nextStep()
  }
}

/**
 * Handle previous step
 */
function handlePrevious(): void {
  tutorial.previousStep()
}

/**
 * Handle skip tutorial
 */
function handleSkip(): void {
  tutorial.skipTutorial()
  emit('skipped')
}

/**
 * Handle spotlight cutout update
 */
function handleTargetFound(): void {
  if (spotlightRef.value?.cutout) {
    cutoutRect.value = { ...spotlightRef.value.cutout }
  }
}

/**
 * Handle target not found
 */
function handleTargetNotFound(): void {
  cutoutRect.value = null
}

/**
 * Keyboard event handler
 */
function handleKeydown(event: KeyboardEvent): void {
  if (!isActiveForContext.value) return

  // Don't capture keyboard events when user is typing in an input field
  const activeElement = document.activeElement
  const isTyping = activeElement instanceof HTMLInputElement ||
    activeElement instanceof HTMLTextAreaElement ||
    activeElement?.getAttribute('contenteditable') === 'true'

  if (isTyping) return

  switch (event.key) {
    case 'ArrowRight':
    case 'Enter':
    case ' ':
      event.preventDefault()
      handleNext()
      break

    case 'ArrowLeft':
      event.preventDefault()
      handlePrevious()
      break

    case 'Escape':
      event.preventDefault()
      handleSkip()
      break
  }
}

/**
 * Start the tutorial for this context
 */
function startTutorial(): void {
  tutorial.startTutorial(props.context)
  emit('started')
}

// Watch for step changes to update cutout
watch(
  () => tutorial.currentStep.value,
  () => {
    // Reset cutout when step changes, let spotlight recalculate
    cutoutRect.value = null
  }
)

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Expose start function for parent components
defineExpose({
  startTutorial
})
</script>

<template>
  <Teleport to="body">
    <Transition name="tutorial-fade">
      <div
        v-if="isActiveForContext"
        class="interface-guide-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Interface Guide Tutorial"
      >
        <!-- Spotlight overlay with cutout -->
        <TutorialSpotlight
          ref="spotlightRef"
          :target="tutorial.currentStep.value?.target || null"
          :padding="tutorial.currentStep.value?.spotlightPadding || 8"
          @target-found="handleTargetFound"
          @target-not-found="handleTargetNotFound"
        />

        <!-- Tooltip with step content -->
        <TutorialTooltip
          :step="tutorial.currentStep.value"
          :cutout="cutoutRect"
          :is-first-step="tutorial.isFirstStep.value"
          :is-last-step="tutorial.isLastStep.value"
          @next="handleNext"
          @previous="handlePrevious"
          @skip="handleSkip"
        />
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.interface-guide-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10001;
  pointer-events: none;
}

/* Make interactive elements clickable */
.interface-guide-overlay :deep(.tutorial-tooltip) {
  pointer-events: auto;
}

/* Fade transition */
.tutorial-fade-enter-active,
.tutorial-fade-leave-active {
  transition: opacity 0.3s ease;
}

.tutorial-fade-enter-from,
.tutorial-fade-leave-to {
  opacity: 0;
}
</style>
